import { createContext, useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { getComponentNameAndData } from '../helpers/pageComponentLayout'

// Components that run a renewal-journey status check on mount and may redirect the member
// elsewhere. While one of these is still deciding, the sibling CMS blocks and status banners
// are hidden so a redirect-bound member never sees a flash of page content before navigation.
const GATE_SOURCES = ['pendingApprovalPage', 'renewalsEmbedForm']

// Backstop window before the gate is forced open, per gate-source. The Pending Approval page runs a
// long poll-and-wait (up to 180s) for the async D365 status flip, so its backstop sits just above that
// window; the renewal form's journey-status poll is short (~10s, with its own portal-home fallback) and
// keeps the 15s safety. Guarantees even a wedged status check can never leave the page blank.
const GATE_BACKSTOP_MS = { pendingApprovalPage: 185000, renewalsEmbedForm: 15000 }
const DEFAULT_GATE_BACKSTOP_MS = 15000

export const JourneyGateContext = createContext({
  gateActive: false,
  gateSource: null,
  gateNested: false,
  releaseGate: () => {},
})

/**
 * Finds the first gate-source component in the raw page components array — top-level
 * entries and entries nested in a container's `componentData.memberAreaComponents` list
 * (`nested: true`). Pure data scan (no window access) so it is safe in the SSR pass.
 */
function scanForGateSource(components) {
  for (const entry of components ?? []) {
    const { typeKey, payload } = getComponentNameAndData(entry)
    if (GATE_SOURCES.includes(typeKey)) {
      return { source: typeKey, nested: false }
    }
    for (const child of payload?.memberAreaComponents ?? []) {
      const childKey = getComponentNameAndData(child).typeKey
      if (GATE_SOURCES.includes(childKey)) {
        return { source: childKey, nested: true }
      }
    }
  }
  return null
}

/**
 * Holds the journey gate for the page. The gate closes when the page contains a component
 * that may redirect the member (GATE_SOURCES), and is released by that component once it
 * knows the member is staying. One-way latch: once open it never re-closes, so late state
 * churn in the source component can't blank the page again.
 */
export function JourneyGateProvider({ components, children }) {
  const [gate, setGate] = useState(() => scanForGateSource(components))

  const releaseGate = useCallback(() => setGate(null), [])

  // Backstop: force the gate open after GATE_BACKSTOP_MS in case the source component
  // never releases it (failed fetch, unexpected status), so the page can never stay blank.
  useEffect(() => {
    if (!gate) return
    const backstopMs = GATE_BACKSTOP_MS[gate.source] ?? DEFAULT_GATE_BACKSTOP_MS
    const timerId = setTimeout(releaseGate, backstopMs)
    return () => clearTimeout(timerId)
  }, [gate, releaseGate])

  return (
    <JourneyGateContext.Provider
      value={{
        gateActive: !!gate,
        gateSource: gate?.source ?? null,
        gateNested: !!gate?.nested,
        releaseGate,
      }}
    >
      {children}
    </JourneyGateContext.Provider>
  )
}

JourneyGateProvider.propTypes = {
  components: PropTypes.arrayOf(PropTypes.object),
  children: PropTypes.node,
}
